import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "../hooks/use-toast";
import FullScreenLoader from "../components/FullScreenLoader";
import { useLanguage } from "../contexts/LanguageContext";

const SUPPORT_TICKET_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/supportTicket";

export function SupportTicketDialog({ open, onOpenChange, onSubmitted }) {
  const { t } = useLanguage();
  const registerData = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const authToken = registerData?.authToken;

  const [ticket, setTicket] = useState({
    company:
      registerData?.companyName ||
      registerData?.company?.companyName ||
      t("Company"),
    contactName:
      registerData?.fullName ||
      registerData?.user?.fullName ||
      t("Contact Name"),
    email: registerData?.email || registerData?.user?.email || "",
    phone: registerData?.phone || registerData?.user?.phone || "",
    plan: registerData?.planOption || registerData?.user?.planOption || "Core",
    category: "",
    priority: "",
    subject: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!ticket.subject.trim() || !ticket.category || !ticket.priority) {
      toast({
        title: t("Please fill in all required fields"),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(SUPPORT_TICKET_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { authorization: authToken } : {}),
        },
        body: JSON.stringify(ticket),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || t("Failed to submit ticket."));
      }
      toast({
        title: t("Support ticket submitted"),
        description: `${t("support.ticketSubmittedPrefix")} "${
          ticket.subject
        }" ${t("support.ticketSubmittedSuffix")}`,
      });
      setTicket((prev) => ({
        ...prev,
        category: "",
        priority: "",
        subject: "",
        description: "",
      }));
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: t("Submit failed"),
        description: e.message || t("Could not submit support ticket."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {loading && <FullScreenLoader />}
        <DialogHeader>
          <DialogTitle className="font-display">
            {t("Submit Support Ticket")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("Company")}</Label>
              <Input value={ticket.company} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t("Plan")}</Label>
              <Input value={ticket.plan} disabled className="bg-muted" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("Contact Name")}</Label>
              <Input value={ticket.contactName} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t("Email")}</Label>
              <Input value={ticket.email} disabled className="bg-muted" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("Phone")}</Label>
            <Input value={ticket.phone} disabled className="bg-muted" />
          </div>

          <hr className="border-border" />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("Category *")}</Label>
              <Select
                value={ticket.category}
                onValueChange={(v) => setTicket((p) => ({ ...p, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("Select category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">
                    {t("Technical Issue")}
                  </SelectItem>
                  <SelectItem value="billing">
                    {t("Billing / Account")}
                  </SelectItem>
                  <SelectItem value="feature">
                    {t("Feature Request")}
                  </SelectItem>
                  <SelectItem value="assessment">
                    {t("Assessment Help")}
                  </SelectItem>
                  <SelectItem value="other">{t("Other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("Priority *")}</Label>
              <Select
                value={ticket.priority}
                onValueChange={(v) => setTicket((p) => ({ ...p, priority: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("Select priority")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t("Low")}</SelectItem>
                  <SelectItem value="medium">{t("Medium")}</SelectItem>
                  <SelectItem value="high">{t("High")}</SelectItem>
                  <SelectItem value="urgent">{t("Urgent")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("Subject *")}</Label>
            <Input
              value={ticket.subject}
              onChange={(e) =>
                setTicket((p) => ({ ...p, subject: e.target.value }))
              }
              placeholder={t("Brief summary of your issue")}
              maxLength={150}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("Description")}</Label>
            <Textarea
              value={ticket.description}
              onChange={(e) =>
                setTicket((p) => ({ ...p, description: e.target.value }))
              }
              placeholder={t(
                "Provide details about your issue, including steps to reproduce if applicable...",
              )}
              rows={4}
              maxLength={2000}
            />
          </div>
        </div>
        <Button onClick={handleSubmit} className="w-full">
          {t("Submit Ticket")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
